<script setup lang="ts">
import { company } from '~/data/company'

// Posts to the PHP endpoint (php/api/contact.php). The CSRF token is fetched on first focus,
// so the session cookie is only set for visitors who actually start writing.
type TextField = 'name' | 'email' | 'company' | 'message'
type Field = TextField | 'consent'
type Status = 'idle' | 'sending' | 'sent' | 'error'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { contact } = useAppConfig()

const form = reactive({ name: '', email: '', company: '', message: '', hp_note: '', consent: false })
const errors = ref<Partial<Record<Field, string>>>({})
const status = ref<Status>('idle')
const generalError = ref('')
const success = useTemplateRef<HTMLElement>('success')
const hydrated = ref(false)
let token = ''
let tokenRequest: Promise<void> | undefined

onMounted(() => (hydrated.value = true))

function fetchToken() {
  tokenRequest ??= $fetch<{ token: string }>(contact.endpoint, { credentials: 'same-origin' })
    .then(r => void (token = r.token))
    .catch(() => void (tokenRequest = undefined))
  return tokenRequest
}

const limits: Record<TextField, [number, number]> = { name: [2, 100], email: [3, 254], company: [0, 120], message: [10, 5000] }

function validate() {
  const next: Partial<Record<Field, string>> = {}
  for (const field of ['name', 'email', 'company', 'message'] as TextField[]) {
    const value = form[field].trim()
    const [min, max] = limits[field]
    if (field !== 'company' && !value) next[field] = 'required'
    else if (value.length < min) next[field] = 'too_short'
    else if (value.length > max) next[field] = 'too_long'
  }
  if (!next.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'invalid'
  if (!form.consent) next.consent = 'consent_required'
  errors.value = next
  return Object.keys(next).length === 0
}

async function submit() {
  generalError.value = ''
  if (!validate()) {
    await nextTick()
    document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
    return
  }
  status.value = 'sending'
  try {
    await fetchToken()
    // No token means the endpoint was unreachable: report it as a server problem, not an expired session.
    if (!token) throw Object.assign(new Error('No CSRF token'), { data: { error: 'server' } })
    await $fetch(contact.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      body: { ...form, token, locale: locale.value },
    })
    status.value = 'sent'
    // The form unmounts: move focus to the confirmation so it is announced and focus isn't lost.
    await nextTick()
    success.value?.focus()
  }
  catch (e: unknown) {
    const data = (e as { data?: { error?: string, fields?: Partial<Record<Field, string>> } }).data
    status.value = 'error'
    // Server-side field errors are shown at the fields; keys this form doesn't render fall
    // through to the generic message, so a rejected submit never fails silently.
    const fieldErrors = Object.fromEntries(
      Object.entries(data?.error === 'validation' && data.fields ? data.fields : {})
        .filter(([key]) => key === 'consent' || Object.hasOwn(limits, key)),
    ) as Partial<Record<Field, string>>
    if (Object.keys(fieldErrors).length) {
      errors.value = fieldErrors
      await nextTick()
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    const code = data?.error === 'rate_limited' || data?.error === 'forbidden' ? data.error : 'server'
    if (code === 'forbidden') tokenRequest = undefined
    generalError.value = t(`contact.form.errors.${code}`, { email: company.email })
  }
}

const fields: { name: TextField, type: string, autocomplete: string, required: boolean }[] = [
  { name: 'name', type: 'text', autocomplete: 'name', required: true },
  { name: 'email', type: 'email', autocomplete: 'email', required: true },
  { name: 'company', type: 'text', autocomplete: 'organization', required: false },
]
const inputClass = 'block w-full border-2 border-muted bg-white px-4 py-3 text-ink transition-colors focus:border-ink focus:outline-none aria-[invalid=true]:border-[#b3261e]'
</script>

<template>
  <section aria-labelledby="form-title">
    <h2
      id="form-title"
      class="mb-8 text-2xl"
    >
      {{ t('contact.form.title') }}
    </h2>

    <p
      v-if="status === 'sent'"
      ref="success"
      role="status"
      tabindex="-1"
      class="border-l-4 border-brand-deep bg-sky p-6 text-lg text-ink outline-none"
    >
      {{ t('contact.form.success') }}
    </p>

    <form
      v-else
      novalidate
      class="space-y-6"
      @submit.prevent="submit"
      @focusin.once="fetchToken"
    >
      <noscript>
        <p class="bg-sky p-4">
        {{ t('contact.form.noJs', { email: company.email }) }}
        </p>
      </noscript>
      <div
        v-for="f in fields"
        :key="f.name"
      >
        <label
          :for="`cf-${f.name}`"
          class="mb-2 block font-semibold text-ink"
        >
          {{ t(`contact.form.${f.name}`) }}<span
            v-if="f.required"
            aria-hidden="true"
          > *</span>
        </label>
        <input
          :id="`cf-${f.name}`"
          v-model="form[f.name]"
          :name="f.name"
          :type="f.type"
          :autocomplete="f.autocomplete"
          :required="f.required"
          :aria-invalid="errors[f.name] ? 'true' : undefined"
          :aria-describedby="errors[f.name] ? `cf-${f.name}-error` : undefined"
          :class="inputClass"
        >
        <p
          v-if="errors[f.name]"
          :id="`cf-${f.name}-error`"
          class="mt-2 text-sm font-semibold text-[#b3261e]"
        >
          {{ t(`contact.form.errors.${errors[f.name]}`) }}
        </p>
      </div>
      <div>
        <label
          for="cf-message"
          class="mb-2 block font-semibold text-ink"
        >{{ t('contact.form.message') }}<span aria-hidden="true"> *</span></label>
        <textarea
          id="cf-message"
          v-model="form.message"
          name="message"
          rows="7"
          required
          :aria-invalid="errors.message ? 'true' : undefined"
          :aria-describedby="errors.message ? 'cf-message-error' : undefined"
          :class="inputClass"
        />
        <p
          v-if="errors.message"
          id="cf-message-error"
          class="mt-2 text-sm font-semibold text-[#b3261e]"
        >
          {{ t(`contact.form.errors.${errors.message}`) }}
        </p>
      </div>
      <!-- Honeypot: invisible to people and assistive tech, tempting for bots. -->
      <div
        class="absolute -left-[9999px] h-px w-px overflow-hidden"
        aria-hidden="true"
      >
        <label for="cf-hp">{{ t('contact.form.website') }}</label>
        <input
          id="cf-hp"
          v-model="form.hp_note"
          name="hp_note"
          type="text"
          tabindex="-1"
          autocomplete="off"
        >
      </div>
      <div>
        <div class="flex items-start gap-3 text-sm">
          <input
            id="cf-consent"
            v-model="form.consent"
            name="consent"
            type="checkbox"
            value="1"
            required
            class="mt-1 size-5 shrink-0 cursor-pointer accent-brand-deep"
            :aria-invalid="errors.consent ? 'true' : undefined"
            :aria-describedby="errors.consent ? 'cf-consent-error' : undefined"
          >
          <i18n-t
            keypath="contact.form.privacy"
            tag="label"
            for="cf-consent"
            class="cursor-pointer"
          >
            <template #email>
              {{ company.email }}
            </template>
            <template #link>
              <NuxtLink
                :to="localePath('/datenschutz')"
                class="link"
              >{{ t('contact.form.privacyLink') }}</NuxtLink>
            </template>
          </i18n-t>
        </div>
        <p
          v-if="errors.consent"
          id="cf-consent-error"
          class="mt-2 text-sm font-semibold text-[#b3261e]"
        >
          {{ t(`contact.form.errors.${errors.consent}`) }}
        </p>
      </div>
      <p
        v-if="generalError"
        role="alert"
        class="border-l-4 border-[#b3261e] bg-[#fdf1f0] p-4 font-semibold text-[#8c1d18]"
      >
        {{ generalError }}
      </p>
      <button
        type="submit"
        class="btn-solid disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!hydrated || !form.consent || status === 'sending'"
      >
        {{ status === 'sending' ? t('contact.form.sending') : t('contact.form.submit') }}
      </button>
    </form>
  </section>
</template>
